import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Store } from '@ngrx/store';

/**
 * Store imports.
 */
import * as HomeAction from '../../../featureModules/dashboard/featureModules/home/store/actions/home.actions';
import * as HomeSelector from '../../../featureModules/dashboard/featureModules/home/store/selectors/home.selectors';
import * as LoginSelector from '../../../store/selectors/login/login.selectors';

/**
 * Constant imports.
 */
import {
  AE_FORM_TYPES,
  APPROVAL_LIST,
  COUNTRIES_LIST,
  PATTERN,
  TH_SERVIER_PRIVATE_PROCESS,
  TH_SERVIER_PROCESS,
  TH_SERVIER_PUBLIC_PROCESS,
  USER_TYPE
} from '../../../constant/app.constant';
import { PharmaNameService } from '../../../services/pharma/pharma-name.service';

@Component({
  selector: 'pap-patient-report',
  templateUrl: './patient-report.component.html',
  styleUrls: ['./patient-report.component.scss']
})
export class PatientReportComponent implements OnInit, OnDestroy, AfterViewInit {

  // ==================== FORM GROUPS ====================

  /**
   * Main form containing reportData form
   */
  public patientReportForm: FormGroup;

  public transactionForm: FormGroup;

  /**
   * Parent form containing all sections as nested FormGroups
   */
  public reportData: FormGroup;

  /**
   * Patient Details Section - Contains patientId AND registeredProgram (merged)
   */
  public patientForm: FormGroup;

  /**
   * Report Details Section - Contains interactionDate, interactionId, and all report fields
   */
  public reportForm: FormGroup;

  /**
   * Relevant Files Section - Contains file uploads
   */
  public relevantLabTestForm: FormGroup;

  public reportType: string = '';

  // ==================== FORM GETTERS (for template access) ====================

  /**
   * Getter for patientDetails controls (used as 'form' in template)
   */
  get form() {
    return this.patientForm?.controls || {};
  }

  // ==================== COMPONENT PROPERTIES ====================

  public userActionForApproval: boolean = false;
  public userActionForSendForReview: boolean = false;
  public userActionForRejection: boolean = false;
  public userActionForSubmit: boolean = false;
  public userActionForSubmitError: boolean = false;
  public userActionForExistingApplication: boolean = false;
  public userActionForNotApprovedApplication: boolean = false;
  public userRole: string;
  public userType = USER_TYPE;
  public aeStatus = APPROVAL_LIST;
  public aeReportStatus = APPROVAL_LIST;
  public aeReport: any;
  public aeCurrentStatus: string;
  public arFormTypes = AE_FORM_TYPES;
  public formsToShow: { [name: string]: string } = {};
  public isSuccessModal = false;
  public changeStatusLoader = false;
  public rejectionRemarks: string = '';
  public sendForReviewRemarks: string = '';

  // Patient Form related
  public patientIdErr: boolean = false;
  public patientDetailsErrorMessages: string = '';
  public selectedPharmaProgram: any = { id: '', name: '', code: '' };
  public filterRegisteredProgramList: any[] = [];
  public registeredProgramList: any[] = [];
  public selectedGender: any;

  // Report Data Form related
  public interactionDate: Date;

  // Relevant Lab Test Form related
  public showRelevantFiles: boolean = true;
  public prescriptionEditCard: any[] = [];
  public fileErrorMessage: string = '';
  public disabled: boolean = false;

  // Other properties
  public aeRemarks: any;
  public aeFormData: any;
  public ackNumber: string;
  public pharmaCode: string;
  public process: string = '';
  public pageFunctionality: string;

  // Subscriptions
  private subscriptions: Subscription = new Subscription();
  private pharmaNameChangeSub: Subscription;
  private activatedRouteDataSub: Subscription;


  public amountUnit: any[] = [
    { id: 'THB', name: 'THB' }
  ];

  public yesNoOptions: any[] = [
    { id: 'YES', name: 'Yes' },
    { id: 'NO', name: 'No' }
  ];


  /**
   * Constructor
   */
  constructor(
    private store: Store,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    public pharmaNameChange: PharmaNameService
  ) {
    this.store.dispatch(HomeAction.resetAeSubmitRes());
    this.store.dispatch(HomeAction.resetAeFormList());
    this.store.dispatch(HomeAction.resetAeReport());
    this.initializeForms();
    this.initializeComponent();
  }

  /**
   * Initialize component
   */
  ngOnInit(): void {
    this.patientDetailsErrorMessages = '';
    this.subscribeToStore();
    this.fetchPharmaRegisteredProgram();
  }

  /**
   * After view init
   */
  ngAfterViewInit(): void {
    this.subscribeToPharmaNameChange();
  }

  /**
   * Initialize all forms with parent reportData form structure
   * Patient Details and Registered Program are merged into single section
   */
  private initializeForms(): void {

    let patientReport: any = sessionStorage.getItem('patientReport');
    if (patientReport) {
      patientReport = JSON.parse(patientReport);
      this.reportType = patientReport?.reportType?.id || '';
    }
    /**
     * initialize the patient report form
     */
    this.patientReportForm = this.formBuilder.group({
      process: [this.process, Validators.required],
      reportType: ['', Validators.required],
      patientReport: [1, Validators.required],
      invoiceNumber: ['', this.reportType === 'REDEMPTION' ? Validators.required : null],
      invoiceAmount: ['', this.reportType === 'REDEMPTION' ? Validators.required : null],
      patientForm: this.formBuilder.group({
        patientId: ['', Validators.required],
        registeredProgramName: ['', Validators.required],
        programId: ['', Validators.required],
      }),
      reportForm: this.formBuilder.group({
        interactionDate: ['', Validators.required],
        interactionId: [''],
        applicationReportCreated: ['', this.reportType === 'REDEMPTION' ? Validators.required : null],
        applicationReportId: ['', this.reportType === 'REDEMPTION' ? Validators.required : null],
        testDate: ['', this.reportType === 'REDEMPTION' ? Validators.required : null],
        invoiceDate: ['', this.reportType === 'REDEMPTION' ? Validators.required : null],
        reimbursementNumber: ['', this.reportType === 'REDEMPTION' ? Validators.required : null],
        reimbursementAmountUnit: ['THB', this.reportType === 'REDEMPTION' ? Validators.required : null],
        continueTreatment: ['',this.reportType === 'REDEMPTION' ? Validators.required : null],
        aeFromCtScan: ['',this.reportType === 'REDEMPTION' ? Validators.required : null],
        aeReportNumber: [''],
      }),
      relevantLabTestForm: this.formBuilder.group({
        relevantFiles: ['']
      })
    });

    this.transactionForm = this.formBuilder.group({
      bankDetailsApprovalDate: [''],
      consentLink: [''],
      patientConsentDate: [''],
      transactionCode: [''],
      transactionDate: ['']
    });

    this.patientForm = this.patientReportForm.get('patientForm') as FormGroup;
    this.reportForm = this.patientReportForm.get('reportForm') as FormGroup;
    this.relevantLabTestForm = this.patientReportForm.get('relevantLabTestForm') as FormGroup;

    if (patientReport) {
      const data: any = patientReport;
      this.patientReportForm.get('reportType').patchValue(data?.reportType?.id);
      this.reportForm.get('interactionDate').patchValue(new Date(data?.interactionDate));
      this.reportForm.get('interactionId').patchValue(data?.interactionId);
    }
  }

  /**
   * Initialize component properties
   */
  private initializeComponent(): void {
    const urlSegmentArr = this.router.url.split('/');
    this.process = sessionStorage.getItem('process') || '';
    this.pharmaNameChange.setProgramPage(true);
    this.pageFunctionality = urlSegmentArr[urlSegmentArr.length - 1];
    if (this.pageFunctionality === 'view') {
      this.disabled = true;
    }
    if (localStorage.getItem('pharmaCode')) {
      this.pharmaCode = localStorage.getItem('pharmaCode');
    }
  }

  /**
   * Subscribe to store observables
   */
  private subscribeToStore(): void {
    // User credentials subscription
    this.subscriptions.add(
      this.store.select(LoginSelector.getUserCredential).subscribe(data => {
        if (data) {
          const helper = new JwtHelperService();
          const decodedToken = helper.decodeToken(data.accessToken);
          this.userRole = decodedToken.role;
        }
      })
    );

    this.subscriptions.add(this.store.select(HomeSelector.getPharmDetails).subscribe(res => {
      if (res) {
        sessionStorage.setItem('process', res.process);
        this.process = res?.process;
        this.patientReportForm.get('process')?.setValue(this.process);
      }
    }))

    // AE Submit Response subscription
    this.subscriptions.add(
      this.store.select(HomeSelector.getAeSubmitRes).subscribe(res => {
        if (res) {
          this.changeStatusLoader = false;
          if (res === APPROVAL_LIST.RE_REVIEW || res === APPROVAL_LIST.REJECTED) {
            this.userActionForRejection = false;
            this.userActionForSendForReview = false;
            this.resetAeForm();
          } else if (res === APPROVAL_LIST.APPROVED) {
            this.userActionForApproval = true;
            this.userActionForSubmit = false;
          } else {
            this.userActionForSubmit = res;
          }
          this.isSuccessModal = true;
        } else if (res === false) {
          this.userActionForSubmitError = false;
          this.isSuccessModal = true;
        }
      })
    );

    // AE Form List subscription
    this.subscriptions.add(
      this.store.select(HomeSelector.getAeFormList).subscribe(list => {
        if (list && list.length) {
          this.formsToShow = {};
          for (const form of list) {
            this.formsToShow[form] = form;
          }
        }
      })
    );

    // Patient Detail subscription for autofill (merged with program)
    this.subscriptions.add(
      this.store.select(HomeSelector.getPatientDetail).subscribe(patientDetail => {
        if (patientDetail) {
          this.patientIdErr = false;
          if (patientDetail?.length === 0) {
            this.patientIdErr = true;
          }
          // Autofill program details when patient is found
          if (patientDetail.programName) {
            this.patientForm.get('registeredProgramName')?.setValue(patientDetail.programName);
            const filterOption = this.filterRegisteredProgramList.find(option => option.name === patientDetail.programName);
            if (filterOption) {
              this.selectedPharmaProgram = filterOption;
            }
          }
          if (patientDetail.programId) {
            this.patientForm.get('programId')?.setValue(patientDetail.programId);
            if (this.patientForm.get('patientId')?.value) {
              this.getPatientBankStatus(this.patientForm.get('patientId')?.value, patientDetail.programId, this.pharmaCode);
            }
            this.store.dispatch(HomeAction.setPatientDetails({ patientDetails: null }));
          }
        }
      })
    );

    this.subscriptions.add(this.store.select(HomeSelector.getPatientBankStatus).subscribe(res => {
      if (res) {
        if (this.reportType === 'REDEMPTION' && (res.data !== 'Approved')) {
          this.isSuccessModal = true;
          this.userActionForNotApprovedApplication = true;
        } else if (res.data === 'APPLICATION_ALREADY_EXISTS') {
          this.isSuccessModal = true;
          this.userActionForExistingApplication = true;
        }
        this.store.dispatch(HomeAction.setPatientBankStatus({ response: null }));
      }
    }));

    this.subscriptions.add(this.store.select(HomeSelector.getHommeError).subscribe(res => {
      if (res) {
        this.patientDetailsErrorMessages = res?.error?.messages?.toString();
      }
    }));

    // Get AE Forms
    this.store.dispatch(HomeAction.getAeForms({
      pharmaId: localStorage.getItem('pharmaCode'),
      process: this.process
    }));
  }

  private getPatientBankStatus(patientId: string, programId: string, pharmaCode: string): void {
    this.store.dispatch(HomeAction.getPatientBankStatus({
      patientId: patientId,
      programId: programId,
      pharmaCode: pharmaCode,
      reportType: this.reportType
    }));
  }

  /**
   * Subscribe to pharma name changes
   */
  private subscribeToPharmaNameChange(): void {
    this.pharmaNameChangeSub = this.pharmaNameChange.data$.subscribe(res => {
      if (res != null) {
        if (this.pageFunctionality !== 'create') {
          this.activatedRouteDataSub = this.activatedRoute.params.subscribe(data => {
            this.getAeReport(data.id);
          });
        }
        this.store.dispatch(HomeAction.setPharmaDetails({ pharmaDetailList: null }));
        this.store.dispatch(HomeAction.getPharmaDetails({ pharmaCode: res }));
      }
    });
  }

  /**
   * Check if field exists
   */
  checkIfFieldExists(fieldName: string): boolean {
    return true; // Default to true, customize as needed
  }

  /**
   * Check if field is mandatory
   */
  checkIfFieldIsMandatory(fieldName: string): boolean {
    if (fieldName === 'patientId' || fieldName === 'registeredProgram') {
      return true;
    }
    return false;
  }

  /**
   * Update form validity for submission
   */
  updateFormValidity(): boolean {
    return this.patientReportForm?.valid || false;
  }

  // ==================== PATIENT FORM METHODS ====================

  private searchPatientTimeout: any;
  /**
   * Search patient by ID
   */
  searchPatient(patientId: string): void {

    if (this.searchPatientTimeout) {
      clearTimeout(this.searchPatientTimeout);
    }

    this.searchPatientTimeout = setTimeout(() => {
      if (patientId) {
        this.store.dispatch(HomeAction.getSearchedPatientId({
          id: patientId,
          pharmaCode: this.pharmaCode
        }));
      }
    }, 500);
  }

  /**
   * Filter registered programs
   */
  filterRegisteredProgram(event: any): void {
    this.filterRegisteredProgramList = this.registeredProgramList;
    const filtered: any[] = [];
    const query = event.query || '';

    for (const programList of this.registeredProgramList) {
      const program = programList;
      if (program.name.toLowerCase().indexOf(query.toLowerCase()) === 0) {
        filtered.push(program);
      }
    }
    this.filterRegisteredProgramList = filtered;
  }

  /**
   * Fetches the registered programs associated with the pharma ID.
   */
  fetchPharmaRegisteredProgram(): void {
    const pharmaId = localStorage.getItem('pharmaId');
    if (!pharmaId) { return; }

    this.store.dispatch(HomeAction.getPharmProgramList({
      keyword: '',
      limit: '200',
      pharma: pharmaId
    }));

    this.subscriptions.add(
      this.store.select(HomeSelector.getPharmaProgramList).subscribe(data => {
        if (!data) { return; }

        this.registeredProgramList = data.list?.map(obj => ({
          id: obj.Program?.Id,
          name: obj.Program?.Name,
          code: obj.Program?.Code
        })) || [];
        this.filterRegisteredProgramList = this.registeredProgramList;
      })
    );
  }


  public onEmitRelevantData(event: any): void {
    this.relevantLabTestForm.get('relevantFiles').setValue(event?.relevantFiles);
  }

  /**
   * Submit AE Report
   * Maps all sections under reportData as JSON structure
   */
  aeReportSubmit(): void {
    if (this.updateFormValidity()) {

      this.changeStatusLoader = true;

      if (this.pageFunctionality === 'edit') {
        this.store.dispatch(HomeAction.updateAeReport({
          id: this.aeFormData?.id,
          form: this.patientReportForm.value,
          pharmaCode: this.pharmaCode
        }));
      } else {
        this.store.dispatch(HomeAction.submitAeReport({
          form: this.patientReportForm.value,
          pharmaCode: this.pharmaCode
        }));
      }
    }
  }

  /**
   * Update AE Report Status
   */
  updateStatus(status: string, remarks: string): void {
    this.changeStatusLoader = true;
    this.store.dispatch(HomeAction.updateAeReportStatus({
      id: this.aeFormData?.id,
      status: status,
      reason: remarks.trim()
    }));
  }

  /**
   * Get AE Report
   */
  getAeReport(patientId: string): void {
    this.store.dispatch(HomeAction.getAeReport({ id: patientId }));
    this.subscriptions.add(
      this.store.select(HomeSelector.getAeReport).subscribe(ae => {
        if (ae) {
          this.aeReport = ae?.reportData;
          this.aeRemarks = ae?.remarks;
          this.ackNumber = ae?.ackNumber;
          this.aeCurrentStatus = ae?.status;
          this.aeFormData = ae;
          // Populate forms with existing data
          this.populateForms(ae);
        }
      })
    );
  }

  normalizeDate(date: Date | string): Date {
    if (!date) return null;
    const d = new Date(date);
    return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0));
  }

  onDateSelect(fieldName: string, date: Date): void {
    if (date) {
      const normalized = this.normalizeDate(date);
      this.reportForm.get(fieldName).setValue(normalized);
    }
  }

  /**
   * Populate forms with existing data
   */
  private populateForms(aeData: any): void {
    if (aeData?.reportData) {
      // Populate reportData form
      if (this.patientReportForm) {
        this.reportType = aeData?.reportType;
        this.patientReportForm.patchValue({
          reportType: aeData?.reportType,
          invoiceNumber: aeData?.invoiceNumber,
          invoiceAmount: aeData?.invoiceAmount
        });

        this.patientForm.patchValue({
          patientId: aeData?.patientId,
          registeredProgramName: aeData?.reportData?.patientForm?.registeredProgramName,
          programId: aeData?.programId
        });

        const reportFormData = aeData?.reportData?.reportForm;
        this.reportForm.patchValue({
          interactionDate: this.normalizeDate(reportFormData?.interactionDate),
          interactionId: reportFormData?.interactionId,
          applicationReportCreated: reportFormData?.applicationReportCreated,
          applicationReportId: reportFormData?.applicationReportId,
          testDate: this.normalizeDate(reportFormData?.testDate),
          invoiceDate: this.normalizeDate(reportFormData?.invoiceDate),
          reimbursementNumber: reportFormData?.reimbursementNumber,
          reimbursementAmountUnit: reportFormData?.reimbursementAmountUnit || '',
          continueTreatment: reportFormData?.continueTreatment,
          aeFromCtScan: reportFormData?.aeFromCtScan,
          aeReportNumber: reportFormData?.aeReportNumber
        });

        this.relevantLabTestForm.patchValue({
          relevantFiles: aeData?.reportData?.relevantLabTestForm?.relevantFiles
        });

        const bankDetailsApprovalDate = aeData.bankDetailsApprovalDate 
          ? new Date(aeData.bankDetailsApprovalDate * 1000) 
          : null;
        const patientConsentDate = aeData.patientConsentDate 
          ? new Date(aeData.patientConsentDate * 1000) 
          : null;
        const transactionDate = aeData.transactionDate 
          ? new Date(aeData.transactionDate * 1000) 
          : null;
        
        this.transactionForm.patchValue({
          bankDetailsApprovalDate: bankDetailsApprovalDate || '',
          consentLink: aeData.consentLink,
          patientConsentDate: patientConsentDate || '',
          transactionCode: aeData.transactionCode,
          transactionDate: transactionDate || ''
        });
      }
    }
  }

  /**
   * Reset AE Form
   */
  resetAeForm(): void {
    this.isSuccessModal = false;
    if (!this.userRole || this.userRole === USER_TYPE.APPROVER) {
      this.router.navigate(['/approval/patientReport/list']);
    } else if (this.userRole === USER_TYPE.PAP_SUPERVISOR) {
      this.router.navigate(['/supervisor/patientReport/list']);
    }
  }

  // ==================== UTILITY METHODS ====================

  /**
   * Check if country is Thailand
   */
  checkForThailandCountry(): boolean {
    return localStorage.getItem('country') === COUNTRIES_LIST.THAILAND.value;
  }

  /**
   * Check if country is Malaysia
   */
  checkForMalaysiaCountry(): boolean {
    return localStorage.getItem('country') === COUNTRIES_LIST.MALAYSIA.value;
  }

  /**
   * Check if country is Singapore
   */
  checkForSingaporeCountry(): boolean {
    return localStorage.getItem('country') === COUNTRIES_LIST.SINGAPORE.value ||
      localStorage.getItem('country') === COUNTRIES_LIST.MALAYSIA.value;
  }

  /**
   * Check if process is for Servier chatbot flow
   */
  checkForServierProcess(): boolean {
    return (this.process === TH_SERVIER_PROCESS ||
      this.process === TH_SERVIER_PRIVATE_PROCESS ||
      this.process === TH_SERVIER_PUBLIC_PROCESS);
  }

  /**
   * Check if process is for Daichii
   */
  checkForDaichiiProcess(): boolean {
    return localStorage.getItem('country') === COUNTRIES_LIST.THAILAND.value;
  }

  /**
   * Cleanup on destroy
   */
  ngOnDestroy(): void {
    this.pharmaNameChange.setProgramPage(false);
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
    if (this.pharmaNameChangeSub) {
      this.pharmaNameChangeSub.unsubscribe();
    }
    if (this.activatedRouteDataSub) {
      this.activatedRouteDataSub.unsubscribe();
    }
    if (sessionStorage.getItem('process')) {
      sessionStorage.removeItem('process');
    }
    sessionStorage.removeItem('patientReport');
  }


  /**
   *  to prevent first letter as blank
   */
  userInput(fieldName: string, event: any): void {
    if (event.target.value) {
      let value = event.target.value;
      if (value[0] === ' ' || value[0] === '0') {
        value = value.substring(1);
        this.patientReportForm.get(fieldName).setValue(value.trimStart());
      }
     
      const validPattern = PATTERN.FLOAT_UPTO_TWO_DECIMAL_PLACES;
        
      if (!validPattern.test(value)) {
        const validValue = value.slice(0, -1);
        this.patientReportForm.get(fieldName).setValue(validValue);
        event.preventDefault();
      }
    }
  }
}